/**
 * DOM-side drawing shared by the ZPL sticker and the colour posters. All
 * geometry comes from src/domain/menu-qr/menu-qr-layout.ts; this module only
 * loads images/fonts and paints rectangles.
 */
import { IMenuQrStepsCopy } from '@/domain/menu-qr/menu-qr-copy';
import {
  fitFontSize,
  IMenuQrLayout,
  IMenuQrRect,
  layoutMenuQrFooter,
  TMeasureTextWidth,
} from '@/domain/menu-qr/menu-qr-layout';
import { Globals } from '@/modules/Globals';
import { loadImage } from '@/utils';

export type TMenuQrFontWeight = 400 | 700 | 900;

export const MENU_QR_FOOTER_SPRITE_PATH = `${Globals.basePath}assets/img/qr/qrbottom.png`;

const MENU_QR_TITLE_COLOR = '#444444';
const MENU_QR_FOOTER_LABEL_COLOR = '#000000';
const FONT_LOAD_TIMEOUT_MS = 3000;
const IMAGE_LOAD_TIMEOUT_MS = 15000;

function loadImageWithTimeout(path: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Image load timed out: ${path}`)),
      IMAGE_LOAD_TIMEOUT_MS
    );

    loadImage(path).then(
      (image) => {
        clearTimeout(timer);
        resolve(image);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export function getMenuQrArtworkPath(file: string): string {
  return `${Globals.basePath}assets/img/qr/${file}`;
}

export function getCanvasContext(
  canvas: HTMLCanvasElement
): CanvasRenderingContext2D {
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Could not create a 2D canvas context.');
  }

  return context;
}

let fontsPromise: Promise<void> | null = null;

/**
 * Waits for the Roboto faces used on posters/stickers so text is measured and
 * drawn with the right font. Never throws; gives up after a short timeout so a
 * blocked font CDN cannot block printing (the next call retries).
 */
export function ensureMenuQrFonts(): Promise<void> {
  if (fontsPromise) return fontsPromise;

  const fonts = typeof document !== 'undefined' ? document.fonts : undefined;

  if (!fonts) return Promise.resolve();

  // Only a load that actually produced faces counts; failures are retried next time.
  const loads = Promise.allSettled([
    fonts.load('400 16px Roboto'),
    fonts.load('700 16px Roboto'),
    fonts.load('900 16px Roboto'),
  ]).then((results) =>
    results.some(
      (result) => result.status === 'fulfilled' && result.value.length > 0
    )
  );
  const timeout = new Promise<boolean>((resolve) =>
    setTimeout(() => resolve(false), FONT_LOAD_TIMEOUT_MS)
  );

  fontsPromise = Promise.race([loads, timeout]).then((loaded) => {
    if (!loaded) fontsPromise = null;
  });

  return fontsPromise;
}

const imageCache = new Map<string, Promise<HTMLImageElement | null>>();

/** Loads an image once; failures resolve to null (the element is simply not drawn). */
export function loadOptionalImage(
  path: string | null | undefined
): Promise<HTMLImageElement | null> {
  if (!path) return Promise.resolve(null);

  let promise = imageCache.get(path);

  if (!promise) {
    promise = loadImageWithTimeout(path).catch((error) => {
      imageCache.delete(path);
      console.warn(`Could not load image for the menu QR: ${path}`, error);
      return null;
    });
    imageCache.set(path, promise);
  }

  return promise;
}

export function menuQrFont(fontPx: number, weight: TMenuQrFontWeight): string {
  return `${weight} ${fontPx}px Roboto, Arial, sans-serif`;
}

export function createTitleMeasurer(
  context: CanvasRenderingContext2D,
  text: string,
  weight: TMenuQrFontWeight
): TMeasureTextWidth {
  return (fontPx) => {
    context.font = menuQrFont(fontPx, weight);
    return context.measureText(text).width;
  };
}

/** Draws the image scaled to fit inside the rect, centred, keeping its aspect ratio. */
/** Corner radius of the drawn logo as a fraction of its shorter side (square logos look harsh unrounded). */
export const MENU_QR_LOGO_CORNER_RATIO = 0.1;

function traceRoundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));

  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

/**
 * Draws the image scaled to fit inside `rect` (aspect kept, centred). With
 * `cornerRatio` the drawn image is clipped to a rounded rectangle whose radius is
 * that fraction of its shorter side.
 */
export function drawContainedImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  rect: IMenuQrRect,
  cornerRatio = 0
) {
  // An image without intrinsic size (e.g. a dimensionless SVG) cannot be fitted.
  if (!image.width || !image.height) return;

  const scale = Math.min(rect.width / image.width, rect.height / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  const x = rect.x + (rect.width - width) / 2;
  const y = rect.y + (rect.height - height) / 2;

  context.save();

  if (cornerRatio > 0) {
    traceRoundedRect(
      context,
      x,
      y,
      width,
      height,
      Math.min(width, height) * cornerRatio
    );
    context.clip();
  }

  context.drawImage(image, x, y, width, height);
  context.restore();
}

export interface IMenuQrTextPlacement {
  centerX: number;
  y: number;
  fontPx: number;
  maxWidth: number;
}

/** Draws centred text at exactly `fontPx`, letting the canvas condense it to `maxWidth`. */
export function drawCenteredText(
  context: CanvasRenderingContext2D,
  text: string,
  placement: IMenuQrTextPlacement,
  weight: TMenuQrFontWeight,
  baseline: 'top' | 'alphabetic',
  color: string
) {
  context.font = menuQrFont(placement.fontPx, weight);
  context.fillStyle = color;
  context.textAlign = 'center';
  context.textBaseline = baseline;
  context.fillText(text, placement.centerX, placement.y, placement.maxWidth);
}

/** Draws centred text, shrinking the font until it fits `maxWidth`. */
export function drawFittedText(
  context: CanvasRenderingContext2D,
  text: string,
  placement: IMenuQrTextPlacement,
  weight: TMenuQrFontWeight,
  baseline: 'top' | 'alphabetic',
  color: string
) {
  const fontPx = fitFontSize(
    placement.fontPx,
    placement.maxWidth,
    createTitleMeasurer(context, text, weight)
  );

  drawCenteredText(
    context,
    text,
    { ...placement, fontPx },
    weight,
    baseline,
    color
  );
}

export interface IMenuQrOverlayParts {
  qrImage: HTMLImageElement;
  title: string | null;
  titleWeight: TMenuQrFontWeight;
  logoImage: HTMLImageElement | null;
  footerSprite: HTMLImageElement | null;
  steps: IMenuQrStepsCopy | null;
}

/** Paints QR, title, logo and steps footer according to a computed layout. */
export function drawMenuQrOverlay(
  context: CanvasRenderingContext2D,
  layout: IMenuQrLayout,
  parts: IMenuQrOverlayParts
) {
  const previousSmoothing = context.imageSmoothingEnabled;

  context.imageSmoothingEnabled = false;
  context.drawImage(
    parts.qrImage,
    layout.qr.x,
    layout.qr.y,
    layout.qr.width,
    layout.qr.height
  );
  context.imageSmoothingEnabled = previousSmoothing;

  // The layout already fitted the title; drawing it at that size keeps the block centred.
  if (parts.title !== null) {
    drawCenteredText(
      context,
      parts.title,
      {
        centerX: layout.title.centerX,
        y: layout.title.top,
        fontPx: layout.title.fontPx,
        maxWidth: layout.title.maxWidth,
      },
      parts.titleWeight,
      'top',
      MENU_QR_TITLE_COLOR
    );
  }

  if (layout.logo && parts.logoImage) {
    drawContainedImage(
      context,
      parts.logoImage,
      layout.logo,
      MENU_QR_LOGO_CORNER_RATIO
    );
  }

  if (layout.footer && parts.steps) {
    const footer = layoutMenuQrFooter(layout.footer);
    const labels = [parts.steps.scan, parts.steps.order, parts.steps.enjoy];

    if (parts.footerSprite) {
      const { stripSource, stripDest } = footer;

      context.drawImage(
        parts.footerSprite,
        stripSource.x,
        stripSource.y,
        stripSource.width,
        stripSource.height,
        stripDest.x,
        stripDest.y,
        stripDest.width,
        stripDest.height
      );
    }

    footer.labels.forEach((label, index) => {
      drawFittedText(
        context,
        labels[index],
        {
          centerX: label.centerX,
          y: label.baselineY,
          fontPx: label.fontPx,
          maxWidth: label.maxWidth,
        },
        700,
        'alphabetic',
        MENU_QR_FOOTER_LABEL_COLOR
      );
    });
  }
}
